/* global BigInt */
// src/pages/MintNFTStudioPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import {
    generateSigner,
    createGenericFile,
    sol,
    publicKey as umiPublicKey,
} from '@metaplex-foundation/umi';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';
import { base58 } from '@metaplex-foundation/umi/serializers';
import { create as createCoreNft, addPlugin, fetchCollection, mplCore } from '@metaplex-foundation/mpl-core';
import { irysUploader } from '@metaplex-foundation/umi-uploader-irys';
import { useWallet } from '@solana/wallet-adapter-react';
import { useCreatorPublishing } from '../../hooks/useCreatorPublishing';
import { authenticatePublishingWallet } from '../../utils/digitalAssetsClient';
import { Link } from 'react-router-dom';
import { useServiceFeePolicy } from '../../hooks/useServiceFeePolicy';
import { addStudioServiceFee } from '../../utils/studioMintFee';
import ServiceFeeNotice from '../ServiceFeeNotice';
import { formatUnits } from '../../utils/serviceFees';

// Irys endpoints
const DEVNET_IRYS = 'https://devnet.irys.xyz';
const MAINNET_IRYS = 'https://node1.irys.xyz';


// ----- UI (drag & drop) -----
const dropStyles = {
    base: {
        border: '2px dashed #1d3f43',
        borderRadius: 12,
        padding: 16,
        textAlign: 'center',
        cursor: 'pointer',
        background: '#fff',
        transition: 'background 120ms',
    },
    active: { background: '#e6fffa' },
    preview: {
        width: '100%',
        maxHeight: 300,
        objectFit: 'contain',
        borderRadius: 12,
        marginTop: 8,
    },
};

// ----- helpers for Royalties (Core requires shares sum to 100 as integers) -----
const pctToBp = (pct) => {
    const n = Number(pct);
    const clamped = Number.isFinite(n) ? Math.max(0, Math.min(20, n)) : 0; // 0..20%
    return Math.round(clamped * 100); // % -> basis points
};

/** Build a Royalties plugin config.
 * ownerPk = connected wallet (minter). otherCreators = [{address, percentage}] (percent of shares out of 100).
 * royaltyPercent = total royalties (0..20 %).
 */
const normalizeRoyalties = (ownerPk, royaltyPercent, otherCreators, toUmiPk) => {
    const basisPoints = pctToBp(royaltyPercent ?? 0);
    if (basisPoints <= 0) return { basisPoints: 0, creators: [] };

    // Collect valid others.
    const others = [];
    for (const c of otherCreators || []) {
        const pct = Number(c?.percentage ?? 0);
        const addr = String(c?.address || '').trim();
        if (!addr || !Number.isFinite(pct) || pct <= 0) continue;
        try {
            const k = toUmiPk(addr).toString(); // validate address parseable
            others.push({ address: k, pct });
        } catch {
            // skip invalid
        }
    }

    // Cap others to 99 to ensure owner gets at least 1.
    let sumOthers = others.reduce((s, x) => s + x.pct, 0);
    if (sumOthers > 99) {
        others.forEach((o) => (o.pct = (o.pct / sumOthers) * 99));
        sumOthers = 99;
    }
    let ownerPct = 100 - Math.round(sumOthers);
    if (ownerPct < 1) ownerPct = 1;

    // Merge duplicates and round.
    const bucket = new Map();
    const addShare = (addr, pct) => {
        const k = toUmiPk(addr).toString();
        bucket.set(k, (bucket.get(k) ?? 0) + pct);
    };
    addShare(ownerPk, ownerPct);
    others.forEach((o) => addShare(o.address, o.pct));

    let creators = [...bucket.entries()].map(([address, pct]) => ({
        address,
        percentage: Math.max(0, Math.round(pct)),
    }));

    // Drift correction to make sum === 100.
    let drift = 100 - creators.reduce((s, c) => s + c.percentage, 0);
    if (drift !== 0 && creators.length > 0) {
        creators[0].percentage = Math.max(0, creators[0].percentage + drift);
    }
    creators = creators.filter((c) => c.percentage > 0);

    if (creators.length === 0) {
        creators = [{ address: toUmiPk(ownerPk).toString(), percentage: 100 }];
    }

    return {
        basisPoints,
        creators: creators.map((c) => ({ address: toUmiPk(c.address), percentage: c.percentage })),
    };
};

export default function MintNFTStudioPage() {
    const walletContext = useWallet();
    const { publicKey, wallet, connected } = walletContext;
    const { collections, space } = useCreatorPublishing(publicKey?.toBase58?.() || '');
    const [collectionId, setCollectionId] = useState('');
    useEffect(() => { if (!collectionId && space?.defaultCollectionId) setCollectionId(space.defaultCollectionId); }, [collectionId, space?.defaultCollectionId]);
    const selectedCollection = collections.find(item => item.id === collectionId);
    const { policy: fees, loading: feesLoading, error: feesError } = useServiceFeePolicy();
    const [feeQuote, setFeeQuote] = useState(null);
    const walletAddress = useRef('');
    walletAddress.current = publicKey?.toBase58?.() || '';

    // Network + Irys
    const rpcEndpoint = process.env.REACT_APP_RPC_ENDPOINT || 'https://api.devnet.solana.com';
    const isDevnet = rpcEndpoint.includes('devnet');
    const irysAddress = isDevnet ? DEVNET_IRYS : MAINNET_IRYS;

    // Form state (mirrors NewMintNFTComponentForm, but mints immediately).
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [attributes, setAttributes] = useState([]);
    const [file, setFile] = useState(null);

    // Drag & drop + preview
    const [previewUrl, setPreviewUrl] = useState('');
    const [isDragActive, setIsDragActive] = useState(false);
    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (f && f.type.startsWith('image/')) {
            setFile(f);
            const url = URL.createObjectURL(f);
            setPreviewUrl(url);
        }
    };
    const onDrop = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        setIsDragActive(false);
        const f = evt.dataTransfer?.files?.[0];
        if (f && f.type.startsWith('image/')) {
            setFile(f);
            const url = URL.createObjectURL(f);
            setPreviewUrl(url);
        }
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const [royaltyPercent, setRoyaltyPercent] = useState(7); // 0..20 (%)
    const [otherCreators, setOtherCreators] = useState([]); // [{address, percentage}]

    // Derived shares for UI
    const othersTotal = otherCreators.reduce((s, r) => s + (Number(r.percentage) || 0), 0);
    const ownerSharePct = Math.max(0, 100 - Math.min(100, othersTotal));

    // UX state
    const [status, setStatus] = useState('');
    const [error, setError] = useState('');
    const [minting, setMinting] = useState(false);
    const [steps, setSteps] = useState({
        imageUploaded: false,
        metadataUploaded: false,
        serviceFeePaid: false,
        nftCreated: false,
        setRoyalties: false,
    });
    const [txSig, setTxSig] = useState('');
    const [assetPubkey, setAssetPubkey] = useState('');
    const [imageUri, setImageUri] = useState('');
    const [metadataUri, setMetadataUri] = useState('');

    // UMI instance
    const umi = useMemo(() => {
        const base = createUmi(rpcEndpoint).use(mplCore());
        if (wallet?.adapter) base.use(walletAdapterIdentity(wallet.adapter));
        base.use(irysUploader({ address: irysAddress }));
        return base;
    }, [wallet?.adapter, rpcEndpoint, irysAddress]);

    const addAttr = () => setAttributes((prev) => [...prev, { name: '', value: '' }]);
    const updateAttr = (i, field, value) =>
        setAttributes((prev) => prev.map((a, idx) => (idx === i ? { ...a, [field]: value } : a)));
    const removeAttr = (i) => setAttributes((prev) => prev.filter((_, idx) => idx !== i));

    const addCreator = () => setOtherCreators((prev) => [...prev, { address: '', percentage: 0 }]);
    const updateCreator = (i, field, value) =>
        setOtherCreators((prev) =>
            prev.map((c, idx) => (idx === i ? { ...c, [field]: field === 'percentage' ? Number(value) : value } : c))
        );
    const removeCreator = (i) => setOtherCreators((prev) => prev.filter((_, idx) => idx !== i));

    // Convert chosen file -> GenericFile
    const toGenericFile = useCallback(async () => {
        if (!file) return null;
        const buf = new Uint8Array(await file.arrayBuffer());
        const mime = file.type || 'image/png';
        return createGenericFile(buf, file.name || 'image', {
            tags: [{ name: 'Content-Type', value: mime }],
        });
    }, [file]);

    const handleMint = async () => {
        setError('');
        setFeeQuote(null);
        setStatus('');
        setTxSig('');
        setAssetPubkey('');
        setImageUri('');
        setMetadataUri('');
        setSteps({ imageUploaded: false, metadataUploaded: false, serviceFeePaid: false, nftCreated: false, setRoyalties: false });

        if (!connected || !publicKey || !wallet?.adapter) {
            setError('Connect a wallet first.');
            return;
        }
        if (!name.trim() || !description.trim() || !file) {
            setError('Please fill in name, description, and add an image.');
            return;
        }
        if (othersTotal > 100) {
            setError('Other creators percentages must sum to ≤ 100.');
            return;
        }

        if (feesLoading || feesError) { setError(feesError || 'Wait for service fees to load.'); return; }
        const payerAddress = publicKey.toBase58();
        try {
            setMinting(true);
            await authenticatePublishingWallet(walletContext);
            if (!selectedCollection || selectedCollection.creatorUserId !== publicKey.toString()) throw new Error('Select one of your Space collections before minting.');

            // 1) Upload artwork to Irys
            setStatus('Preparing and uploading image…');
            const generic = await toGenericFile();
            if (!generic) throw new Error('No image file.');
            const [imgUri] = await umi.uploader.upload([generic]);
            if (!imgUri) throw new Error('Image upload failed.');
            setImageUri(imgUri);
            setSteps((s) => ({ ...s, imageUploaded: true }));

            // 2) Upload metadata to Irys
            const contentType = generic.tags?.find((t) => t.name === 'Content-Type')?.value || 'image/png';
            const metadata = {
                name: name.trim(),
                description: description.trim(),
                image: imgUri,
                attributes: attributes
                    .filter((a) => a.name?.trim()?.length)
                    .map((a) => ({ trait_type: a.name.trim(), value: String(a.value ?? '') })),
                properties: { files: [{ uri: imgUri, type: contentType }], category: 'image' },
            };

            setStatus('Uploading metadata…');
            const mdUri = await umi.uploader.uploadJson(metadata);
            if (!mdUri) throw new Error('Metadata upload failed.');
            setMetadataUri(mdUri);
            setSteps((s) => ({ ...s, metadataUploaded: true }));

            // 3) Build a single transaction: optional service fee -> create -> royalties
            setStatus('Building transaction…');
            const asset = generateSigner(umi);

            const collection = await fetchCollection(umi, umiPublicKey(selectedCollection.onChainCollectionAddress));
            let builder = createCoreNft(umi, {
                asset, collection, owner: umiPublicKey(publicKey.toString()),
                name: name.trim(), uri: mdUri,
            });

            // Royalties — owner = minter (connected wallet)
            try {
                const { basisPoints, creators } = normalizeRoyalties(selectedCollection.creatorUserId, royaltyPercent, otherCreators, umiPublicKey);
                if (basisPoints > 0 && creators.length > 0) {
                    builder = builder.add(
                        addPlugin(umi, {
                            asset: asset.publicKey,
                            plugin: {
                                type: 'Royalties',
                                basisPoints,
                                creators,
                                ruleSet: { type: 'None' },
                            },
                        })
                    );
                }
            } catch (royErr) {
                console.warn('Royalties plugin skipped:', royErr);
            }

            setStatus('Sending transaction…');
            const preparedFee = await addStudioServiceFee({ builder, umi, wallet: walletContext, policy: fees });
            builder = preparedFee.builder;
            setFeeQuote(preparedFee.quote);
            if (walletAddress.current !== payerAddress) throw new Error('Wallet changed. Please retry.');
            const receipt = await builder.sendAndConfirm(umi);
            if (receipt.result?.value?.err) throw new Error('The mint transaction failed.');
            const sig =
                typeof receipt === 'string'
                    ? receipt
                    : typeof receipt?.signature === 'string'
                    ? receipt.signature
                    : base58.deserialize(receipt?.signature || new Uint8Array())[0];

            setTxSig(sig);
            setAssetPubkey(asset.publicKey.toString());
            setSteps((s) => ({
                ...s,
                serviceFeePaid: BigInt(preparedFee.quote.solFeeLamports) > 0n,
                nftCreated: true,
                setRoyalties: pctToBp(royaltyPercent) > 0,
            }));

            setStatus('NFT created! It may take a bit for metadata to propagate.');
        } catch (e) {
            console.error(e);
            setError(e.message || 'Mint failed.');
        } finally {
            setMinting(false);
        }
    };

    // Airdrop for devnet
    const [airdropping, setAirdropping] = useState(false);
    const handleAirdrop = async () => {
        if (!isDevnet || !umi?.identity?.publicKey) return;
        try {
            setAirdropping(true);
            setStatus('Airdropping 1 SOL to your devnet wallet…');
            await umi.rpc.airdrop(umi.identity.publicKey, sol(1));
            setStatus('Airdrop complete.');
        } catch (e) {
            console.error(e);
            setError('Airdrop failed (RPC rate limited?).');
        } finally {
            setAirdropping(false);
        }
    };

    const canMint =
        !!selectedCollection &&
        selectedCollection.cluster === (isDevnet ? 'devnet' : 'mainnet-beta') &&
        !!name.trim() &&
        !!description.trim() &&
        !!file &&
        Number.isFinite(Number(royaltyPercent)) &&
        royaltyPercent >= 0 &&
        royaltyPercent <= 20 &&
        othersTotal <= 100;

    return (
        <div className="muulti-container" style={{ maxWidth: 820, margin: '0 auto', padding: 16 }}>
            <h1 style={{ marginBottom: 16 }}>Mint a Single NFT</h1>
            <p>Mint to your own creator wallet. Paid collector releases are published from your media component controls.</p>
            <label>Collection<select className="muulti-input" value={collectionId} onChange={event => setCollectionId(event.target.value)}>
                <option value="">Select a collection</option>{collections.filter(item => item.cluster === (isDevnet ? 'devnet' : 'mainnet-beta')).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select></label>
            {!collections.length && <Link to="/collection-creation">Create a collection for this Space</Link>}



            <ServiceFeeNotice kind="studioMint" quote={feeQuote} />
            <div className="new-component-form mint-form" style={{ background: '#fff', padding: 16, borderRadius: 12 }}>
                <h3>Name</h3>
                <input
                    className="muulti-input"
                    type="text"
                    placeholder="NFT Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{ marginBottom: 8 }}
                />
                <h4>Description</h4>
                <textarea
                    className="muulti-textarea"
                    placeholder="NFT Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={{ marginBottom: 12 }}
                />



                {/* Drag & Drop + Preview */}
                <div
                    style={{ ...dropStyles.base, ...(isDragActive ? dropStyles.active : {}) }}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onClick={() => document.getElementById('nft-file-input').click()}
                >
                    <strong>Drag & drop an image here</strong>
                    <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                        or click to choose a file
                    </div>
                    {previewUrl ? <img src={previewUrl} alt="preview" style={dropStyles.preview} /> : null}
                </div>
                <input
                    id="nft-file-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onPick}
                />


                <div className="attributes-list" style={{ marginBottom: 12 }}>
                    <h4>Attributes</h4>
                    {attributes.map((attr, idx) => (
                        <div key={idx} className="attribute-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 92px', gap: 8, marginBottom: 8 }}>
                            <input
                                className="muulti-input"
                                placeholder="Attribute Name"
                                value={attr.name}
                                onChange={(e) => updateAttr(idx, 'name', e.target.value)}
                            />
                            <input
                                className="muulti-input"
                                placeholder="Attribute Value"
                                value={attr.value}
                                onChange={(e) => updateAttr(idx, 'value', e.target.value)}
                            />
                            <button onClick={() => removeAttr(idx)} className="muulti-button cancel-button">Remove</button>
                        </div>
                    ))}
                    <button onClick={addAttr} className="muulti-button">Add Attribute</button>
                </div>


                {/* Royalties */}
                <div style={{ marginTop: 8 }}>
                    <h4>Royalties (secondary sales)</h4>
                    <label className="muulti-label" style={{ display: 'block' }}>
                        Total Royalties (%)
                        <input
                            className="muulti-input"
                            type="number"
                            min="0"
                            max="20"
                            step="0.1"
                            value={royaltyPercent}
                            onChange={(e) => setRoyaltyPercent(Number(e.target.value))}
                        />
                    </label>
                    <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                        {royaltyPercent}% total. The original Space creator is always included; additional recipients below must sum to ≤ 100%.
                    </div>

                    <div style={{ marginTop: 12, padding: 8, border: '1px solid #eee', borderRadius: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>You (auto)</div>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                            Percentage: <strong>{ownerSharePct}%</strong> (100% − sum(other creators))
                        </div>
                    </div>

                    <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                        <div style={{ fontWeight: 600 }}>Other creators</div>
                        {otherCreators.map((r, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 92px', gap: 8, alignItems: 'center' }}>
                                <input
                                    className="muulti-input"
                                    placeholder="Recipient public key"
                                    value={r.address}
                                    onChange={(e) => updateCreator(i, 'address', e.target.value)}
                                />
                                <input
                                    className="muulti-input"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={r.percentage}
                                    onChange={(e) => updateCreator(i, 'percentage', e.target.value)}
                                />
                                <button className="muulti-button cancel-button" onClick={() => removeCreator(i)}>Remove</button>
                            </div>
                        ))}
                        <button className="muulti-button" onClick={addCreator}>Add Creator</button>
                        <div style={{ fontSize: 12, color: othersTotal <= 100 ? '#16a34a' : '#b91c1c' }}>
                            Sum of other creators: <b>{othersTotal}%</b> (must be ≤ 100%)
                        </div>
                    </div>
                </div>

                <div className="muulti-container-button-container" style={{ marginTop: 16 }}>
                    {isDevnet && (
                        <button
                            className="muulti-button"
                            onClick={handleAirdrop}
                            disabled={airdropping || !connected}
                            style={{ marginRight: 8 }}
                        >
                            {airdropping ? 'Airdropping…' : 'Devnet Airdrop 1 SOL'}
                        </button>
                    )}
                    <button className="muulti-button" onClick={handleMint} disabled={minting || !connected || !canMint || feesLoading || !!feesError}>
                        {minting ? 'Minting…' : 'Mint NFT'}
                    </button>
                </div>
            </div>

            {/* Checklist */}
            <div style={{ marginTop: 16, padding: 12, border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Mint steps</div>
                <ul style={{ textAlign: 'left', listStyle: 'none', paddingLeft: 18, lineHeight: 1.7 }}>
                    <li>{steps.imageUploaded ? '✅' : '⬜'} Upload image to Irys</li>
                    <li>{steps.metadataUploaded ? '✅' : '⬜'} Upload metadata to Irys</li>
                    <li>{steps.serviceFeePaid ? 'Paid:' : 'Service fee:'} {feeQuote ? formatUnits(feeQuote.solFeeLamports) + ' SOL' : 'shown before wallet approval'}</li>
                    <li>{steps.nftCreated ? '✅' : '⬜'} Create NFT on Solana (Core)</li>
                    <li>{steps.setRoyalties ? '✅' : '⬜'} Add Royalties</li>
                </ul>


            {/* Links */}
            {txSig && (
                <div style={{ marginTop: 12 }}>
                    <div><strong>Transaction:</strong></div>
                    <a
                        href={`https://explorer.solana.com/tx/${txSig}?cluster=${isDevnet ? 'devnet' : 'mainnet'}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        View on Solana Explorer
                    </a>
                </div>
            )}
            {assetPubkey && (
                <div style={{ marginTop: 8 }}>
                    <div><strong>Asset:</strong></div>
                    <a
                        href={`https://core.metaplex.com/explorer/${assetPubkey}?env=${isDevnet ? 'devnet' : 'mainnet'}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        View on Metaplex Explorer
                    </a>
                </div>
            )}
            {(imageUri || metadataUri) && (
                <div style={{ marginTop: 8 }}>
                    <div><strong>Uploads (Irys)</strong></div>
                    {imageUri && <div>Image: <a href={imageUri} target="_blank" rel="noreferrer">{imageUri}</a></div>}
                    {metadataUri && <div>Metadata: <a href={metadataUri} target="_blank" rel="noreferrer">{metadataUri}</a></div>}
                </div>
            )}
                {error && <p className="error" style={{ color: '#b91c1c' }}>{error}</p>}
                {status && <p style={{ color: '#0f766e' }}>{status}</p>}
            </div>
        </div>
    );
}
